import { STATE_LABELS, type Effort, type Item, type ItemState, type Urgency } from "../lib/item";

const STATE_STYLES: Record<string, string> = {
  "next:human": "bg-accent/15 text-accent-soft",
  "next:agent": "bg-ink-800 text-ink-300",
  "next:external": "bg-warn/15 text-warn",
  "next:implement": "bg-accent/20 text-accent-soft",
  "next:plan": "bg-accent/20 text-accent-soft",
  "next:build": "bg-emerald-500/15 text-emerald-300",
  "done:accepted": "bg-ink-800 text-ink-400",
  "done:dropped": "bg-ink-800 text-ink-500",
  "done:superseded": "bg-ink-800 text-ink-500",
};

/** Left-edge colour of a card — the one signal you can scan while scrolling. */
export const URGENCY_STRIPE: Record<Urgency, string> = {
  high: "border-l-4 border-l-danger",
  normal: "border-l-4 border-l-transparent",
  low: "border-l-4 border-l-ink-800",
};

const URGENCY_STYLES: Record<Urgency, string> = {
  high: "bg-danger/15 text-danger",
  normal: "bg-ink-850 text-ink-500",
  low: "bg-ink-850 text-ink-600",
};

const URGENCY_LABELS: Record<Urgency, string> = {
  high: "urgent",
  normal: "normal",
  low: "later",
};

export { URGENCY_LABELS };

export function UrgencyChip({ urgency }: { urgency: Urgency }) {
  return <span className={`chip ${URGENCY_STYLES[urgency]}`}>{URGENCY_LABELS[urgency]}</span>;
}

export function EffortChip({ effort }: { effort: Effort }) {
  const labels: Record<Effort, string> = { S: "small", M: "medium", L: "large" };
  return <span className="chip bg-ink-850 text-ink-400">{labels[effort]}</span>;
}

/** "3d" / "today" — how long the item has been sitting there. */
export function ageLabel(days: number): string {
  if (days <= 0) return "today";
  if (days === 1) return "1d";
  return `${days}d`;
}

export function daysSince(date: string | undefined): number | null {
  if (!date) return null;
  const t = Date.parse(date);
  if (Number.isNaN(t)) return null;
  return Math.max(0, Math.floor((Date.now() - t) / 86_400_000));
}

export function StateChip({ state }: { state: ItemState }) {
  return (
    <span className={`chip ${STATE_STYLES[state] ?? "bg-ink-800 text-ink-400"}`}>
      {state}
    </span>
  );
}

export function RiskChip({ risk }: { risk: "low" | "medium" | "high" }) {
  const styles = {
    low: "bg-ink-800 text-ink-400",
    medium: "bg-warn/15 text-warn",
    high: "bg-danger/15 text-danger",
  } as const;
  return <span className={`chip ${styles[risk]}`}>{risk} risk</span>;
}

/** One row in the inbox list. */
export function ItemRow({
  item,
  unanswered,
  personName,
  onOpen,
}: {
  item: Item;
  unanswered: number;
  /** Display name of the assignee, when the item is assigned to someone. */
  personName?: string;
  onOpen: () => void;
}) {
  const title = item.title;

  const age = daysSince(item.created);

  return (
    <button
      onClick={onOpen}
      className={`card w-full p-4 text-left transition-colors hover:bg-ink-850 active:bg-ink-800 ${URGENCY_STRIPE[item.urgency]}`}
    >
      <div className="mb-2 flex flex-wrap items-center gap-1.5">
        <StateChip state={item.state} />
        <span className="chip bg-ink-850 text-ink-500">
          {item.subcategory ? `${item.domain} · ${item.subcategory}` : item.domain}
        </span>
        {item.urgency === "high" && <UrgencyChip urgency="high" />}
        {personName && <span className="chip bg-ink-850 text-ink-400">👤 {personName}</span>}
      </div>
      <div className="text-[15px] font-medium leading-snug text-ink-100">{title}</div>
      <div className="mt-2 flex flex-wrap items-center gap-x-3 gap-y-1 text-xs text-ink-500">
        {item.hasAskBlock ? (
          unanswered > 0 ? (
            <span className="font-medium text-accent-soft">
              {unanswered} to answer
            </span>
          ) : (
            <span>all answered</span>
          )
        ) : (
          <span>no ask block — needs deep review</span>
        )}
        {item.effort && <EffortChip effort={item.effort} />}
        {age !== null && <span title="created">{ageLabel(age)}</span>}
      </div>
    </button>
  );
}

export { STATE_LABELS };
